import { useFlip } from '../../hooks/useFlip';
import type { ChecklistAccount, ChecklistEntry } from '../../lib/checklist';
import { cadenceRank, CATEGORY_LABELS, categoryOfEntry } from '../../lib/checklistGrouping';
import { LeafMark } from '../LeafMark';
import { TaskControl } from './TaskControl';

interface TodayRow {
  entry: ChecklistEntry;
  ownerLabel: string;
}

function flattenAccount(account: ChecklistAccount): TodayRow[] {
  const rows: TodayRow[] = account.tasks.map((entry) => ({ entry, ownerLabel: 'Account' }));
  for (const character of account.characters) {
    for (const entry of character.tasks) {
      rows.push({ entry, ownerLabel: character.name });
    }
  }
  return rows;
}

const CADENCE_LABEL: Record<ChecklistEntry['cadence'], string> = {
  DAILY: 'Daily',
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
};

interface TodayViewProps {
  account: ChecklistAccount | null;
  taskCategoryBySlug: Map<string, string>;
  isPending: boolean;
  isError: boolean;
  onToggle: (entry: ChecklistEntry, count: number) => void;
}

/** The mobile-first "today" surface: everything still open, grouped by cadence, biggest-impact first. */
export function TodayView({ account, taskCategoryBySlug, isPending, isError, onToggle }: TodayViewProps) {
  // Hooks run unconditionally, ahead of this component's several early returns (Rules of Hooks).
  const flipRef = useFlip<HTMLDivElement>();

  if (isPending) {
    return <p className="text-sm text-text-muted">Loading today’s checklist…</p>;
  }
  if (isError) {
    return (
      <p role="alert" className="text-sm text-danger">
        Could not load the checklist. Please retry in a moment.
      </p>
    );
  }
  if (!account) {
    return (
      <p className="text-sm text-text-muted">
        No account selected yet — add one under Characters to start tracking tasks.
      </p>
    );
  }

  const rows = flattenAccount(account);
  if (rows.length === 0) {
    return (
      <section className="rounded-xl border border-dashed border-border-strong bg-bg-elevated p-6 text-center">
        <LeafMark className="mx-auto text-text-faint" size={24} />
        <p className="mt-3 font-display text-sm font-semibold text-text">Nothing tracked yet</p>
        <p className="mt-2 text-sm text-text-muted">
          Head to <span className="text-accent">Manage tasks</span> and turn on the dailies and
          weeklies you want to track.
        </p>
      </section>
    );
  }

  const open = rows.filter((r) => !r.entry.done);
  const done = rows.filter((r) => r.entry.done);

  const openByCadence = [...open].sort((a, b) => {
    const rankDiff = cadenceRank(a.entry.cadence) - cadenceRank(b.entry.cadence);
    return rankDiff !== 0 ? rankDiff : a.entry.name.localeCompare(b.entry.name);
  });

  const groups = new Map<ChecklistEntry['cadence'], TodayRow[]>();
  for (const row of openByCadence) {
    const bucket = groups.get(row.entry.cadence);
    if (bucket) {
      bucket.push(row);
    } else {
      groups.set(row.entry.cadence, [row]);
    }
  }

  return (
    <div ref={flipRef} className="space-y-6">
      {open.length === 0 && (
        <div className="flex items-center gap-3 rounded-xl border border-success/30 bg-success/10 p-4">
          <LeafMark className="text-success" size={22} />
          <p className="font-display text-sm font-semibold text-success">All wrapped up for today.</p>
        </div>
      )}

      {[...groups.entries()].map(([cadence, cadenceRows]) => (
        <section key={cadence}>
          <h2 className="mb-2 font-display text-xs font-semibold uppercase tracking-widest text-text-faint">
            {CADENCE_LABEL[cadence]} · still open
          </h2>
          <ul className="space-y-2">
            {cadenceRows.map(({ entry, ownerLabel }) => (
              <li
                key={entry.characterTaskId}
                data-flip-key={String(entry.characterTaskId)}
                className="flex min-h-tap items-center gap-3 rounded-lg border border-border bg-bg-elevated px-3 py-2.5 transition-colors hover:bg-bg-elevated-hover"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-text">{entry.name}</p>
                  <p className="truncate text-xs text-text-faint">
                    {ownerLabel} · {CATEGORY_LABELS[categoryOfEntry(entry, taskCategoryBySlug)]}
                  </p>
                </div>
                <TaskControl
                  count={entry.count}
                  maxCount={entry.maxCount}
                  label={`${entry.name} (${ownerLabel})`}
                  onChange={(count) => onToggle(entry, count)}
                />
              </li>
            ))}
          </ul>
        </section>
      ))}

      {done.length > 0 && (
        <section>
          <h2 className="mb-2 font-display text-xs font-semibold uppercase tracking-widest text-text-faint">
            Done ({done.length})
          </h2>
          <ul className="space-y-2">
            {done.map(({ entry, ownerLabel }) => (
              <li
                key={entry.characterTaskId}
                data-flip-key={String(entry.characterTaskId)}
                className="flex min-h-tap items-center gap-3 rounded-lg border border-border/60 bg-bg-elevated/60 px-3 py-2.5 opacity-70"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-text-muted line-through decoration-text-faint">
                    {entry.name}
                  </p>
                  <p className="truncate text-xs text-text-faint">{ownerLabel}</p>
                </div>
                <TaskControl
                  count={entry.count}
                  maxCount={entry.maxCount}
                  label={`${entry.name} (${ownerLabel})`}
                  onChange={(count) => onToggle(entry, count)}
                />
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
